/**
 * Blok pengiriman pada pembuatan tugas, dan form kegagalan — MURNI.
 *
 * Dua aturan yang menentukan seluruh modul, keduanya berasal dari ADR-0035/ADR-0037 dan bukan
 * dari selera tampilan:
 *
 * 1. **Tugas `courier_delivery` wajib punya blok `delivery`** (`TASK-009`). Tanpa daftar barang
 *    dan penerima, "pengiriman" hanyalah tugas biasa yang menyamar — dan bukti pengiriman tidak
 *    punya apa pun untuk ditautkan.
 *
 * 2. **Alasan gagal WAJIB berupa kode terstruktur, bukan teks bebas.** Ini inti ADR-0035: teks
 *    bebas tidak bisa dihitung, tidak bisa dibandingkan antar-kurir, dan tidak bisa menjadi dasar
 *    perbaikan operasional. Modul ini tidak menyediakan jalan apa pun untuk mengirim alasan
 *    sebagai kalimat.
 *
 * Seperti validasi lain di klien: ini MEMANDU, server tetap MENEGAKKAN.
 */

export interface DeliveryItemDraft {
  readonly lineNo: number;
  readonly itemName: string;
  readonly sku: string;
  readonly quantity: string;
  readonly unit: string;
}

export interface DeliveryBlockDraft {
  readonly deliveryOrderNumber: string;
  readonly customerName: string;
  readonly contactPerson: string;
  readonly contactPhone: string;
  readonly pickupLocationName: string;
  readonly pickupAddress: string;
  readonly deliveryNotes: string;
  readonly items: readonly DeliveryItemDraft[];
}

export interface FormProblem {
  readonly field: string;
  readonly message: string;
}

export function emptyItem(lineNo: number): DeliveryItemDraft {
  return { lineNo, itemName: '', sku: '', quantity: '1', unit: '' };
}

/**
 * Menomori ulang seluruh baris menjadi 1..n.
 *
 * Dipanggil setelah SETIAP penambahan/penghapusan baris. Tanpa ini, menghapus baris tengah
 * meninggalkan lubang (`1, 3, 4`) yang terkirim apa adanya ke server dan muncul di surat jalan
 * sebagai barang yang seolah hilang — orang yang menerima barang akan menghitung ulang dan
 * mengira ada yang kurang.
 */
export function renumber(items: readonly DeliveryItemDraft[]): readonly DeliveryItemDraft[] {
  return items.map((item, index) => ({ ...item, lineNo: index + 1 }));
}

export function removeItem(
  items: readonly DeliveryItemDraft[],
  lineNo: number,
): readonly DeliveryItemDraft[] {
  return renumber(items.filter((item) => item.lineNo !== lineNo));
}

export function addItem(items: readonly DeliveryItemDraft[]): readonly DeliveryItemDraft[] {
  return renumber([...items, emptyItem(items.length + 1)]);
}

/**
 * Benar bila jenis tugas menuntut blok pengiriman.
 *
 * Menerima `requiresProof` — sifat yang DITENTUKAN SERVER per kategori tugas
 * (`task-type-category.ts`), BUKAN kode jenis tugas mentah.
 *
 * Versi lama mencocokkan `taskTypeCode` ke dua string tetap (`DELIVERY`/`COURIER_DELIVERY`).
 * Itu cukup untuk kedua kode sistem bawaan, tapi jenis tugas KUSTOM berkategori
 * Pengiriman/Penjemputan (fitur Setelan Tugas, ADR-0035 C2 — pelanggan boleh membuat jenis
 * tugasnya sendiri) punya kode BEBAS yang tidak pernah cocok salah satu string itu. Akibatnya
 * blok "Barang" tidak pernah muncul saat tugas dibuat, tugas lahir tanpa data pengiriman, dan
 * begitu kurir sampai lokasi ia macet total — `DeliverySection` tidak menampilkan apa pun
 * (termasuk tombol Selesai/Gagal) tanpa data itu, tanpa satu pun galat yang mengatakannya.
 * Ditemukan audit peran 14 Sep 2026 (WKF-003, issue #1156).
 */
export function requiresDeliveryBlock(requiresProof: boolean): boolean {
  return requiresProof;
}

/**
 * Benar bila jenis tugas ini adalah pertukaran berkas penagihan — "tukar faktur".
 *
 * Arahan Owner 26 Agustus 2026: "tukar faktur bukan kirim barang". Jenis ini TIDAK menuntut
 * blok pengiriman — tidak ada barang yang diantar — dan sebagai gantinya menuntut daftar
 * dokumen. Kebalikan persis dari `DELIVERY`.
 *
 * SENGAJA bukan negasi `requiresDeliveryBlock`: sebuah jenis dapat bukan keduanya (Kunjungan,
 * Penjemputan), dan menuliskannya sebagai negasi akan membuat setiap jenis baru diam-diam
 * menuntut daftar dokumen.
 */
export function isTukarFaktur(taskTypeCode: string): boolean {
  return taskTypeCode.trim().toUpperCase() === 'TUKAR_FAKTUR';
}

/**
 * Masalah pada blok pengiriman, terurut sesuai urutan isian di layar.
 *
 * Kuantitas divalidasi sebagai angka positif: `0` barang bukan pengiriman, dan kuantitas
 * negatif adalah salah ketik yang lolos diam-diam sampai barang dihitung di lokasi.
 */
export function validateDeliveryBlock(block: DeliveryBlockDraft): readonly FormProblem[] {
  const problems: FormProblem[] = [];

  if (block.customerName.trim().length === 0) {
    problems.push({ field: 'customerName', message: 'Isi nama pelanggan penerima.' });
  }

  if (block.items.length === 0) {
    problems.push({
      field: 'items',
      message: 'Tambahkan minimal satu barang. Pengiriman tanpa barang tidak dapat dibuktikan.',
    });
  }

  for (const item of block.items) {
    if (item.itemName.trim().length === 0) {
      problems.push({
        field: `items.${item.lineNo}.itemName`,
        message: `Baris ${item.lineNo}: isi nama barang.`,
      });
    }
    const quantity = Number(item.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      problems.push({
        field: `items.${item.lineNo}.quantity`,
        message: `Baris ${item.lineNo}: jumlah harus angka lebih dari nol.`,
      });
    }
  }

  return problems;
}

// ── Form kegagalan ──────────────────────────────────────────────────────────

export interface ReasonCodeLite {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly requiresDetail: boolean;
  readonly isActive: boolean;
}

/**
 * Kode yang boleh DIPILIH sekarang — hanya yang aktif.
 *
 * Berbeda dari kode yang boleh DITAMPILKAN: attempt lama yang memakai kode kini nonaktif tetap
 * harus terbaca di riwayat. Menyembunyikannya akan membuat kegagalan masa lalu tampak tanpa
 * alasan, dan riwayat kegagalan justru satu-satunya bahan untuk memperbaiki operasional.
 */
export function selectableReasonCodes(codes: readonly ReasonCodeLite[]): readonly ReasonCodeLite[] {
  return codes.filter((code) => code.isActive);
}

/** Label untuk kode apa pun — termasuk yang sudah nonaktif, demi riwayat. */
export function reasonCodeLabel(
  codes: readonly ReasonCodeLite[],
  reasonCodeId: string | null,
): string {
  if (reasonCodeId === null) return 'Tanpa alasan tercatat';
  const found = codes.find((c) => c.id === reasonCodeId);
  if (!found) return 'Alasan tidak dikenal';
  return found.isActive ? found.label : `${found.label} (tidak dipakai lagi)`;
}

export interface FailFormValues {
  readonly reasonCodeId: string;
  readonly detail: string;
}

/**
 * Aturan `requires_detail` — penjaga utama agar alasan gagal tetap bermakna.
 *
 * Kode seperti "lainnya" tidak menjelaskan apa pun tanpa keterangan. Server menegakkannya
 * dengan `TASK-007`; di sini ia dicegah lebih dulu supaya kurir tidak kehilangan isian yang
 * sudah diketik hanya untuk diberi tahu bahwa satu kolom kurang.
 */
export function validateFailForm(
  values: FailFormValues,
  codes: readonly ReasonCodeLite[],
): readonly FormProblem[] {
  const problems: FormProblem[] = [];

  const selected = codes.find((c) => c.id === values.reasonCodeId);
  if (!selected) {
    // Tidak ada jalur "tulis alasan sendiri". Alasan gagal harus berupa kode.
    problems.push({ field: 'reasonCodeId', message: 'Pilih alasan kegagalan dari daftar.' });
    return problems;
  }

  if (!selected.isActive) {
    problems.push({
      field: 'reasonCodeId',
      message: 'Alasan itu sudah tidak dipakai lagi. Pilih alasan yang tersedia.',
    });
  }

  if (selected.requiresDetail && values.detail.trim().length === 0) {
    problems.push({
      field: 'detail',
      message: 'Alasan ini menuntut keterangan. Jelaskan apa yang terjadi.',
    });
  }

  return problems;
}

/** Benar bila isian detail perlu ditampilkan untuk kode yang sedang dipilih. */
export function detailRequiredFor(reasonCodeId: string, codes: readonly ReasonCodeLite[]): boolean {
  return codes.find((c) => c.id === reasonCodeId)?.requiresDetail ?? false;
}

/**
 * Judul tugas yang benar-benar dikirim.
 *
 * ADA KARENA Owner bertanya "Judul diisi apa?" (26 Agustus 2026) — dan pertanyaan itu adalah
 * jawabannya sendiri. Untuk tukar faktur, judul tidak membawa keterangan baru: jenisnya sudah
 * "Tukar Faktur" dan pelanggannya sudah diisi di kolom sendiri. Meminta orang mengarang kalimat
 * yang menggabungkan dua hal yang SUDAH ia ketik adalah pekerjaan tanpa hasil.
 *
 * Lebih buruk: kolom itu WAJIB, dan menolaknya membuat tombol simpan tampak mati.
 *
 * Judul manual tetap menang bila diisi — sebagian orang memberi nomor faktur di situ, dan
 * menimpanya akan menghapus keterangan yang justru paling membedakan.
 */
export function judulTugas(taskTypeCode: string, judul: string, pelanggan: string): string {
  const manual = judul.trim();
  if (manual.length > 0) return manual;
  if (!isTukarFaktur(taskTypeCode)) return '';
  const nama = pelanggan.trim();
  return nama.length > 0 ? `Tukar Faktur — ${nama}` : '';
}
